import { useState } from 'react';
import { LoadError } from '@/components/LoadError';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { Progress } from '@/components/Progress';
import { TaskRow } from '@/components/TaskRow';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/toast-context';
import {
  useCreateTask,
  useArchiveProject,
  useMe,
  useProject,
  useSetTaskStatus,
  useTasks,
} from '@/lib/queries';

export default function ProjectPage() {
  const { t, i18n } = useTranslation();
  const { id = '' } = useParams();
  const toast = useToast();
  const query = useProject(id);
  const tasks = useTasks();
  const me = useMe();
  const setStatus = useSetTaskStatus();
  const archiveProject = useArchiveProject();
  const createTask = useCreateTask();
  const [title, setTitle] = useState('');
  const [error, setError] = useState(false);

  if (query.isError) return <LoadError retry={() => query.refetch()} />;
  if (!query.data) return <p className="text-sm text-muted">{t('common.loading')}</p>;

  const { project, goal } = query.data;
  const projectTasks = (tasks.data ?? []).filter((task) => task.project?.id === project.id);
  const percent =
    project.taskCounts.total === 0
      ? null
      : (project.taskCounts.done / project.taskCounts.total) * 100;

  return (
    <section className="flex flex-col gap-6">
      <div>
        <Link
          to={goal ? `/goals/${goal.id}` : '/goals'}
          className="inline-flex min-h-11 min-w-11 items-center text-sm text-muted hover:text-ink"
        >
          ← {goal ? goal.title : t('goals.title')}
        </Link>
        <h1 className="mt-2 font-serif text-2xl font-semibold">{project.title}</h1>
        <p className="mt-1 flex flex-wrap gap-x-3 text-xs text-muted">
          <span>{t(`project.status.${project.status}`)}</span>
          {project.dueDate && (
            <span>{t('project.due', { date: formatDate(project.dueDate, i18n.language) })}</span>
          )}
          {!goal && <span>{t('project.noGoal')}</span>}
        </p>
      </div>

      <div className="flex flex-col gap-2 rounded-card border border-line bg-surface p-4">
        <Progress
          percent={percent}
          label={t('goal.progressOf', { title: project.title })}
          tone="project"
        />
        <p className="text-xs text-muted">
          {t('goal.tasksDone', { done: project.taskCounts.done, total: project.taskCounts.total })}
        </p>
      </div>

      <form
        className="flex flex-wrap gap-2"
        onSubmit={async (e) => {
          e.preventDefault();
          if (!title.trim()) return;
          setError(false);
          try {
            await createTask.mutateAsync({ title: title.trim(), projectId: project.id });
            setTitle('');
          } catch {
            setError(true);
          }
        }}
      >
        <label className="flex min-w-0 flex-1 flex-col gap-1 text-sm">
          {t('polish.newTask')}
          <input
            value={title}
            maxLength={200}
            onChange={(e) => setTitle(e.target.value)}
            className="min-h-11 rounded-lg border border-line bg-surface px-3 text-base"
          />
        </label>
        <Button type="submit" disabled={createTask.isPending || !title.trim()} className="self-end">
          {t('common.create')}
        </Button>
      </form>
      {error && <p role="alert">{t('polish.draftError')}</p>}
      {tasks.isError ? (
        <LoadError retry={() => tasks.refetch()} />
      ) : tasks.isPending ? (
        <p role="status">{t('common.loading')}</p>
      ) : projectTasks.length === 0 ? (
        <p className="text-sm text-muted">{t('project.noTasks')}</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {projectTasks.map((task) => (
            <TaskRow
              key={task.id}
              task={task}
              today={me.data?.today ?? ''}
              showDefer={false}
              onToggle={() =>
                setStatus.mutate({
                  id: task.id,
                  status: task.status === 'done' ? 'todo' : 'done',
                })
              }
              onDefer={() => {}}
            />
          ))}
        </ul>
      )}

      <div>
        <Button
          size="sm"
          onClick={() => {
            archiveProject.mutate({ id: project.id, archived: !project.archivedAt });
            toast.show({
              message: project.archivedAt ? t('archive.restored') : t('archive.archivedProject'),
              actionLabel: t('common.undo'),
              onAction: () =>
                archiveProject.mutate({ id: project.id, archived: !!project.archivedAt }),
            });
          }}
        >
          {project.archivedAt ? t('archive.restore') : t('archive.archive')}
        </Button>
      </div>
    </section>
  );
}

function formatDate(date: string, locale: string): string {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long' }).format(
    new Date(Date.UTC(y, m - 1, d)),
  );
}
